import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import SessionConstants from '../../config/SessionConstants';
import { TextField, Autocomplete, Button as MuiButton } from '@mui/material';
import APIUtils from '../../utils/APIUtils';
import SuccessMessageModal from '../SuccessMessageModal';
import Oauth from '../../utils/Oauth';
export default function ImpersonationModal({ modalIsOpen, closeModal }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [successMessageModalIsOpen, setSuccessMessageModalIsOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState([]);
  const [currentImpersonatedEmail, setCurrentImpersonatedEmail] = useState(null);

  const onClose = () => {
    closeModal();
  };
  const closeAndRefresh = () => {
    setSuccessMessageModalIsOpen(false)
    window.location.assign('/home');
  }

  // Helper function to save session value only if it exists
  const saveOriginalSessionValueIfPresent = (originalKey, currentKey) => {
    const value = sessionStorage.getItem(currentKey);
    if (value !== null && value !== undefined) {
      sessionStorage.setItem(originalKey, value);
    } else {
      sessionStorage.removeItem(originalKey);
    }
  };

  // Helper function to restore session value, handling null/undefined
  const restoreSessionValue = (key, value) => {
    if (value !== null && value !== undefined && value !== 'null' && value !== 'undefined') {
      sessionStorage.setItem(key, value);
    } else {
      sessionStorage.removeItem(key);
    }
  };

  const returnFocus = () => {
    const el = document.getElementById("userMenuButton");
    if (el) el.focus();
  };

  useEffect(() => {
    if (!modalIsOpen) return;
    let mounted = true;
    setLoading(true);
    setError(null);
    
    // Load currently impersonated user if exists
    const isImpersonating = sessionStorage.getItem(SessionConstants.IS_IMPERSONATING) === 'true';
    if (isImpersonating) {
      const impersonatedEmail = sessionStorage.getItem(SessionConstants.USER_EMAIL);
      setCurrentImpersonatedEmail(impersonatedEmail);
    } else {
      setCurrentImpersonatedEmail(null);
      setSelectedUser(null);
    }
    
    APIUtils.getAllUsers().then(response => {
      if (!mounted) return;
      setUsers(response);
      
      // Set selected user if currently impersonating
      if (isImpersonating) {
        const impersonatedEmail = sessionStorage.getItem(SessionConstants.USER_EMAIL);
        const impersonatedUser = response.find(u => u.email === impersonatedEmail);
        if (impersonatedUser) {
          setSelectedUser(impersonatedUser);
        }
      }
      
      setLoading(false);
    });
    return () => { mounted = false; };
  }, [modalIsOpen]);

  const impersonateUser = (userInfo) => {
     // Save original user data before impersonation (only if not already impersonating)
     const isAlreadyImpersonating = sessionStorage.getItem(SessionConstants.IS_IMPERSONATING) === 'true';
     if (!isAlreadyImpersonating) {
       saveOriginalSessionValueIfPresent(SessionConstants.ORIGINAL_USER_EMAIL, SessionConstants.USER_EMAIL);
       saveOriginalSessionValueIfPresent(SessionConstants.ORIGINAL_USER_FIRSTNAME, SessionConstants.USER_FIRSTNAME);
       saveOriginalSessionValueIfPresent(SessionConstants.ORIGINAL_USER_LASTNAME, SessionConstants.USER_LASTNAME);
       saveOriginalSessionValueIfPresent(SessionConstants.ORIGINAL_CURRENT_USER, SessionConstants.CURRENT_USER);
       saveOriginalSessionValueIfPresent(SessionConstants.ORIGINAL_PROGRAM_ID, SessionConstants.PROGRAM_ID);
       saveOriginalSessionValueIfPresent(SessionConstants.ORIGINAL_PROGRAM_NAME, SessionConstants.NBS_PROGRAM);
       saveOriginalSessionValueIfPresent(SessionConstants.ORIGINAL_IS_MANAGER, SessionConstants.IS_MANAGER);
       saveOriginalSessionValueIfPresent(SessionConstants.ORIGINAL_IS_SYSADMIN, SessionConstants.IS_SYSADMIN);
     }
     
     sessionStorage.setItem(SessionConstants.IS_IMPERSONATING, true);
     Oauth.setAllSessionVariables(userInfo);
     // Manually set IS_SYSADMIN during impersonation since Oauth.setAllSessionVariables doesn't update it when CURRENT_USER exists
     sessionStorage.setItem(SessionConstants.IS_SYSADMIN, userInfo.roleName === 'SYSADMIN');
   
    // Close the impersonation modal then show a success message with user details
    closeModal();
    setSuccessMessage([
      `User: ${userInfo.fullName}`,
      `Program: ${userInfo.programId}`,
      `Role: ${userInfo.roleName}`,
    ]);
    setSuccessMessageModalIsOpen(true);
    console.log('Impersonation complete. You are now logged in as: ' + sessionStorage.getItem(SessionConstants.USER_EMAIL));
  };
  
  const clearImpersonation = () => {
    // Get original user info from session (stored before impersonation)
    const originalEmail = sessionStorage.getItem(SessionConstants.ORIGINAL_USER_EMAIL);
    const originalFirstName = sessionStorage.getItem(SessionConstants.ORIGINAL_USER_FIRSTNAME);
    const originalLastName = sessionStorage.getItem(SessionConstants.ORIGINAL_USER_LASTNAME);
    const originalCurrentUser = sessionStorage.getItem(SessionConstants.ORIGINAL_CURRENT_USER);
    const originalProgramId = sessionStorage.getItem(SessionConstants.ORIGINAL_PROGRAM_ID);
    const originalProgramName = sessionStorage.getItem(SessionConstants.ORIGINAL_PROGRAM_NAME);
    const originalIsManager = sessionStorage.getItem(SessionConstants.ORIGINAL_IS_MANAGER);
    const originalIsSysAdmin = sessionStorage.getItem(SessionConstants.ORIGINAL_IS_SYSADMIN);
    
    // Restore original user session
    restoreSessionValue(SessionConstants.USER_EMAIL, originalEmail);
    restoreSessionValue(SessionConstants.USER_FIRSTNAME, originalFirstName);
    restoreSessionValue(SessionConstants.USER_LASTNAME, originalLastName);
    restoreSessionValue(SessionConstants.CURRENT_USER, originalCurrentUser);
    restoreSessionValue(SessionConstants.PROGRAM_ID, originalProgramId);
    restoreSessionValue(SessionConstants.NBS_PROGRAM, originalProgramName);
    restoreSessionValue(SessionConstants.IS_MANAGER, originalIsManager);
    restoreSessionValue(SessionConstants.IS_SYSADMIN, originalIsSysAdmin);
    sessionStorage.setItem(SessionConstants.IS_IMPERSONATING, false);
    
    // Clear original user data from session storage
    sessionStorage.removeItem(SessionConstants.ORIGINAL_USER_EMAIL);
    sessionStorage.removeItem(SessionConstants.ORIGINAL_USER_FIRSTNAME);
    sessionStorage.removeItem(SessionConstants.ORIGINAL_USER_LASTNAME);
    sessionStorage.removeItem(SessionConstants.ORIGINAL_CURRENT_USER);
    sessionStorage.removeItem(SessionConstants.ORIGINAL_PROGRAM_ID);
    sessionStorage.removeItem(SessionConstants.ORIGINAL_PROGRAM_NAME);
    sessionStorage.removeItem(SessionConstants.ORIGINAL_IS_MANAGER);
    sessionStorage.removeItem(SessionConstants.ORIGINAL_IS_SYSADMIN);
    
    // Clear impersonation state
    setSelectedUser(null);
    setCurrentImpersonatedEmail(null);
    
    closeModal();
    setSuccessMessage([
      `Impersonation cleared`,
    ]);
    setSuccessMessageModalIsOpen(true);
  };
  
  const handleConfirm = () => {
    if (selectedUser && selectedUser.email !== currentImpersonatedEmail) {
      // Impersonate new user
      impersonateUser(selectedUser);
    } else if (!selectedUser && currentImpersonatedEmail) {
      // Clear impersonation
      clearImpersonation();
    }
  };
  return (
    <div>
      <Modal
        className="modal"
        overlayClassName="modalOverlay"
        aria-modal="true"
        contentLabel="Impersonation"
        shouldCloseOnOverlayClick={true}
        isOpen={modalIsOpen}
        onRequestClose={onClose}
        onAfterClose={returnFocus}
      >
        <div className="modalHeader">Impersonation</div>
        <div className="modalContent center">
          <div className="modalSection center"></div>  
          <div className="modalSection center">
            {loading && <div>Loading users...</div>}
            {error && <div className="error">{error}</div>}
            {!loading && !error && (
              <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                  <Autocomplete
                    fullWidth
                    id="impersonationSelect"
                    options={users}
                    value={selectedUser}
                    onChange={(event, newValue) => {
                      setSelectedUser(newValue);
                    }}
                    getOptionLabel={(option) => 
                      `${option.firstName} ${option.lastName} - ${option.programId} - ${option.roleName}`
                    }
                    isOptionEqualToValue={(option, value) => option.email === value?.email}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select User to Impersonate"
                        placeholder="Search by name, program, or role"
                      />
                    )}
                    disablePortal
                  />
                  {selectedUser && (
                    <MuiButton
                      variant="outlined"
                      onClick={() => setSelectedUser(null)}
                      style={{ minWidth: '80px', height: '56px' }}
                    >
                      Clear
                    </MuiButton>
                  )}
                </div>
              </div>
            )}
          </div>

        </div>
        <div className="modalButtons">
          <button 
            className="modalButton primaryButton" 
            onClick={handleConfirm} 
            disabled={!selectedUser && !currentImpersonatedEmail}
          >
            {!selectedUser && currentImpersonatedEmail ? 'Clear Impersonation' : 'Confirm'}
          </button>
          <button className="modalButton" onClick={onClose}>Cancel</button>
        </div>
      </Modal>
      <SuccessMessageModal
        modalHeader="User Impersonation Settings Updated Successfully"
        closeModal={closeAndRefresh}
        modalIsOpen={successMessageModalIsOpen}
        message={successMessage}
      />
    </div>
  );
}
